
import React from 'react';
import { Tabs, Tab, Box, useMediaQuery, useTheme } from '@mui/material';
import {
  FlightTakeoff as TripsIcon,
  PhotoLibrary as PhotosIcon,
  Explore as CommunityIcon,
  Place as DestinationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

interface TabConfig {
  label: string;
  icon: React.ReactElement;
  path: string;
  featureFlag?: string | string[]; // Single flag or array of flags (any must be enabled)
}

const tabs: TabConfig[] = [
  { label: 'My Trips', icon: <TripsIcon />, path: '/trips' },
  { label: 'My Photos', icon: <PhotosIcon />, path: '/photos' },
  { label: 'Community', icon: <CommunityIcon />, path: '/community', featureFlag: ['community_public', 'community_friends'] },
  { label: 'Destinations', icon: <DestinationsIcon />, path: '/destinations', featureFlag: 'destinations' },
];

const TabNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isEnabled, isLoading } = useFeatureFlags();

  // Check if a tab should be enabled based on feature flags
  const isTabEnabled = (tab: TabConfig): boolean => {
    if (!tab.featureFlag) return true; // No flag requirement

    if (Array.isArray(tab.featureFlag)) {
      // At least one of the flags must be enabled
      return tab.featureFlag.some(flag => isEnabled(flag) === true);
    }

    return isEnabled(tab.featureFlag) === true;
  };

  // Get filtered tabs (only enabled ones)
  const visibleTabs = tabs.filter((tab) => isLoading || isTabEnabled(tab));

  // Determine active tab based on current path
  const getCurrentTabIndex = (): number => {
    const currentPath = location.pathname;

    // Check for exact matches first
    const exactIndex = visibleTabs.findIndex(tab => tab.path === currentPath);
    if (exactIndex !== -1) return exactIndex;

    // Check for path prefixes (e.g., /trips/123 should match /trips)
    const prefixIndex = visibleTabs.findIndex(tab => currentPath.startsWith(tab.path + '/'));
    if (prefixIndex !== -1) return prefixIndex;

    // Special cases - find index in visible tabs
    if (currentPath === '/explore') {
      const communityIndex = visibleTabs.findIndex(tab => tab.path === '/community');
      return communityIndex !== -1 ? communityIndex : 0;
    }
    if (currentPath.startsWith('/trips')) {
      const tripsIndex = visibleTabs.findIndex(tab => tab.path === '/trips');
      return tripsIndex !== -1 ? tripsIndex : 0;
    }

    return 0; // Default to first visible tab
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tab = visibleTabs[newValue];
    if (tab) {
      navigate(tab.path);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 64, // Below header
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }}
    >
      <Tabs
        value={getCurrentTabIndex()}
        onChange={handleTabChange}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        scrollButtons={isMobile ? 'auto' : false}
        allowScrollButtonsMobile
        sx={{
          maxWidth: 1200,
          margin: '0 auto',
          '& .MuiTab-root': {
            minHeight: 56,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.95rem',
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 600,
            },
            '&.Mui-disabled': {
              color: 'text.disabled',
              opacity: 0.5,
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        }}
      >
{visibleTabs.map((tab) => (
            <Tab
              key={tab.path}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              onClick={() => {
                if (location.pathname !== tab.path) {
                  navigate(tab.path);
                }
              }}
              sx={{
                gap: 1,
                cursor: 'pointer',
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                },
              }}
            />
          ))}
      </Tabs>
    </Box>
  );
};

export default TabNavigation;
